import { FaultCodes, getFaultName } from '@feedflo/fault-codes';

/** Alert Dashboard type filter ids mapped to fault codes for deep links. */
export const ALERT_DASHBOARD_TYPE_TO_FAULT_CODE = {
  'empty pipe': FaultCodes.EMPTY_PIPE,
  'inactive auger': FaultCodes.INACTIVE_AUGER,
  'sudden drop': FaultCodes.SUDDEN_CONSUMPTION_DROP,
  'trending down': FaultCodes.CONSUMPTION_TRENDING_DOWN,
};

export const MESSAGE_HISTORY_MODE_DB_TO_UI = {
  push: 'App',
  email: 'Email',
  sms: 'SMS',
};

export const MESSAGE_HISTORY_MODE_UI_TO_DB = {
  App: 'push',
  Email: 'email',
  SMS: 'sms',
};

/**
 * Build a /message-history URL with optional filter query params.
 * @param {Object} filters
 * @param {string|string[]} [filters.orgId]
 * @param {string|string[]} [filters.barnId]
 * @param {string|string[]} [filters.userId]
 * @param {string|string[]} [filters.mode] - UI labels (App/Email/SMS) or db values (push/email/sms)
 * @param {number|number[]} [filters.type] - fault codes
 * @param {number} [filters.from] - unix seconds
 * @param {number} [filters.to] - unix seconds
 * @param {string} [filters.series] - mode|type|user|barn
 */
export function buildMessageHistoryUrl(filters = {}) {
  const params = new URLSearchParams();

  const appendArray = (key, values) => {
    if (values === undefined || values === null || values === '') {
      return;
    }
    const list = Array.isArray(values) ? values : [values];
    list.filter((v) => v !== undefined && v !== null && v !== '').forEach((v) => params.append(key, String(v)));
  };

  appendArray('orgId', filters.orgId);
  appendArray('barnId', filters.barnId);
  appendArray('userId', filters.userId);

  if (filters.mode !== undefined) {
    const modes = Array.isArray(filters.mode) ? filters.mode : [filters.mode];
    const dbModes = modes.map(
      (m) => MESSAGE_HISTORY_MODE_UI_TO_DB[m] || (MESSAGE_HISTORY_MODE_DB_TO_UI[m] ? m : m),
    );
    appendArray('mode', dbModes);
  }

  appendArray('type', filters.type);

  if (filters.from !== undefined && filters.from !== null) {
    params.set('from', String(filters.from));
  }
  if (filters.to !== undefined && filters.to !== null) {
    params.set('to', String(filters.to));
  }
  if (filters.series) {
    params.set('series', filters.series);
  }

  const query = params.toString();
  return query ? `/message-history?${query}` : '/message-history';
}

function hasFilterValue(value) {
  return value !== undefined && value !== null && value !== '';
}

function getFirstFilterValue(value) {
  if (Array.isArray(value)) {
    return value.find((item) => hasFilterValue(item));
  }

  return value;
}

/**
 * Derive a short action label from message history filter params.
 * @param {Object} filters
 */
export function buildMessageHistoryLabel(filters = {}) {
  const hasOrg = hasFilterValue(filters.orgId);
  const hasBarn = hasFilterValue(filters.barnId);
  const hasUser = hasFilterValue(filters.userId);
  const hasType = hasFilterValue(filters.type);

  if (hasType && hasBarn) {
    return 'Notifications for this alert';
  }

  if (hasOrg && hasBarn && hasUser) {
    return 'Message history for this barn and user';
  }

  if (hasBarn && hasUser) {
    return 'Message history for this barn and user';
  }

  if (hasOrg && hasBarn) {
    return 'Message history for this barn';
  }

  if (hasOrg && hasUser) {
    return 'Message history for this user';
  }

  if (hasOrg) {
    return 'Organization message history';
  }

  if (hasUser) {
    return 'My message history';
  }

  return 'Message history';
}

const safeGetFaultName = (code) => {
  try {
    return getFaultName(code);
  } catch (error) {
    return `Fault Code ${code}`;
  }
};

/**
 * Derive helper copy describing the message history destination.
 * @param {Object} filters
 */
export function buildMessageHistoryDescription(filters = {}) {
  const baseDescription = 'View sent push, email, and SMS notifications with delivery status.';
  const hasType = hasFilterValue(filters.type);
  const hasDateRange = hasFilterValue(filters.from) || hasFilterValue(filters.to);

  if (!hasType && !hasDateRange) {
    return baseDescription;
  }

  if (hasType && hasDateRange) {
    const typeCode = getFirstFilterValue(filters.type);
    return `${baseDescription} Filtered to ${safeGetFaultName(String(typeCode))} for this time range.`;
  }

  if (hasType) {
    const typeCode = getFirstFilterValue(filters.type);
    return `${baseDescription} Filtered to ${safeGetFaultName(String(typeCode))}.`;
  }

  return `${baseDescription} Filtered to this time range.`;
}
